import { useEffect, useState } from 'react';
import {
  ensureAdobeIms,
  signInWithAdobe,
  signOutFromAdobe,
  syncAdobeImsSession } from
'./adobeIms';
import { fetchUserRecord } from '../utils/database';

const SIGNED_OUT_SESSION: AdobeImsSession = {
  accessLevel: 'anonymous',
  accessToken: null,
  canManageData: false,
  isSignedIn: false,
  user: null
};

// Module-level cache so all IMS lifecycle events (onReady, onAccessToken, onProfile)
// that fire on the same page load share one in-flight request and one resolved result.
let _cachedLookupEmail: string | null = null;
let _cachedLookup: Promise<import('../utils/database').UserRecord | null> | null = null;

function getOrFetchUserRecord(email: string) {
  if (email !== _cachedLookupEmail) {
    _cachedLookupEmail = email;
    _cachedLookup = fetchUserRecord(email);
  }
  return _cachedLookup!;
}

function clearUserRecordCache() {
  _cachedLookupEmail = null;
  _cachedLookup = null;
}

async function applyDbAccessLevel(session: AdobeImsSession): Promise<AdobeImsSession> {
  if (!session.isSignedIn || !session.user?.email) {
    return session;
  }

  const userRecord = await getOrFetchUserRecord(session.user.email);
  if (!userRecord) {
    return {
      ...session,
      accessLevel: 'unregistered',
      canManageData: false
    };
  }

  const accessLevel = userRecord.accessLevel;
  return {
    ...session,
    accessLevel,
    canManageData: accessLevel === 'admin' || accessLevel === 'editor'
  };
}

export interface AdobeImsAuthState extends AdobeImsSession {
  error: string | null;
  isLoading: boolean;
  isReady: boolean;
}

export function useAdobeImsAuth() {
  const [state, setState] = useState<AdobeImsAuthState>({
    ...SIGNED_OUT_SESSION,
    error: null,
    isLoading: true,
    isReady: false
  });

  useEffect(() => {
    let isMounted = true;

    const bridge: MyAppAuthBridge = {
      onLogin(session) {
        if (!isMounted) {
          return;
        }

        void applyDbAccessLevel(session).then((enrichedSession) => {
          if (!isMounted) return;
          setState({
            ...enrichedSession,
            error: null,
            isLoading: false,
            isReady: true
          });
        });
      },
      onLogout() {
        if (!isMounted) {
          return;
        }

        clearUserRecordCache();
        setState({
          ...SIGNED_OUT_SESSION,
          error: null,
          isLoading: false,
          isReady: true
        });
      },
      onAuthError(error) {
        if (!isMounted) {
          return;
        }

        setState((currentState) => ({
          ...currentState,
          error: error.message,
          isLoading: false,
          isReady: true
        }));
      }
    };

    window.myAppAuth = bridge;

    void ensureAdobeIms()
      .then(() => syncAdobeImsSession())
      .then((session) => applyDbAccessLevel(session))
      .then((session) => {
        if (!isMounted) {
          return;
        }

        setState({
          ...session,
          error: null,
          isLoading: false,
          isReady: true
        });
      })
      .catch((error: unknown) => {
        if (!isMounted) {
          return;
        }

        const message =
        error instanceof Error ?
        error.message :
        'Unable to initialize Adobe IMS.';

        setState({
          ...SIGNED_OUT_SESSION,
          error: message,
          isLoading: false,
          isReady: true
        });
      });

    return () => {
      isMounted = false;

      if (window.myAppAuth === bridge) {
        delete window.myAppAuth;
      }
    };
  }, []);

  const signIn = async () => {
    setState((currentState) => ({
      ...currentState,
      error: null,
      isLoading: true
    }));

    try {
      await signInWithAdobe();
      setState((currentState) => ({
        ...currentState,
        isLoading: false
      }));
    } catch (error) {
      const message =
      error instanceof Error ?
      error.message :
      'Unable to start Adobe sign-in.';

      setState((currentState) => ({
        ...currentState,
        error: message,
        isLoading: false,
        isReady: true
      }));
    }
  };

  const signOut = async () => {
    try {
      await signOutFromAdobe();
    } catch (error) {
      const message =
      error instanceof Error ?
      error.message :
      'Unable to sign out from Adobe IMS.';

      setState((currentState) => ({
        ...currentState,
        error: message,
        isLoading: false,
        isReady: true
      }));
    }
  };

  return {
    ...state,
    signIn,
    signOut
  };
}
